import { useEffect, useState } from 'react';
import { can, type MeView } from './api.ts';
import { suspensionReason } from './accountSuspension.ts';
import { formatPence, planLines, trialDaysLeft } from './billing.ts';
import { billingApi, type BillingProduct, type BillingSummary } from './billingApi.ts';
import { Package } from './Package.tsx';
import './billing.css';
import './users.css';

const message = (cause: unknown): string =>
  cause instanceof Error ? cause.message : String(cause);

const days = (count: number): string => (count === 1 ? '1 day' : `${count} days`);

interface BillingProps {
  readonly me: MeView;
  /**
   * Drawn on Restore access for a billing-suspended account (`RestoreAccess.tsx`),
   * where the server refuses everything but billing: the package limits and the
   * pointer to each client's Plan tab would both lead to a 403.
   */
  readonly restoring?: boolean;
}

/**
 * What this account pays, client by client, and the way to pay it.
 *
 * Everybody in the account sees the prices. An engineer adding a framework to a
 * client's coverage should know what it costs before asking an owner to buy it,
 * and the bill is a fact about their own account rather than a setting they are
 * being trusted with. Only owners get the two buttons, and both of them leave
 * the console: Checkout and the Customer Portal are Stripe's pages, so no card
 * detail is ever typed into this one.
 *
 * The account's package limits stay underneath. Clients, schedules and seats
 * are still capped by the package whatever each client's plan says, and a limit
 * somebody can see is one they can plan around.
 */
export const Billing = ({ me, restoring = false }: BillingProps) => {
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [products, setProducts] = useState<readonly BillingProduct[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [leaving, setLeaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    void Promise.all([billingApi.billingSummary(), billingApi.billingCatalogue()])
      .then(([nextSummary, catalogue]) => {
        setSummary(nextSummary);
        setProducts(catalogue.products);
      })
      .catch((cause: unknown) => setError(message(cause)));
  }, []);

  /*
   * `assign`, not `replace`: the back button should bring an owner who changes
   * their mind on Stripe's page back here. The flag stays set on success,
   * because the page is being left and a button that re-enabled itself in the
   * moment before it went would invite a second session.
   */
  const leaveFor = async (start: () => Promise<{ url: string }>) => {
    setLeaving(true);
    setActionError(null);
    try {
      const { url } = await start();
      window.location.assign(url);
    } catch (cause) {
      setActionError(message(cause));
      setLeaving(false);
    }
  };

  const owner = can(me, 'billing.manage');

  return (
    <div className="bl-stack">
      <section className="us-panel" aria-labelledby="billing-heading">
        <h2 id="billing-heading">Billing</h2>

        {error !== null && <div className="error us-error">{error}</div>}
        {error === null && summary === null && <p className="note">Loading billing…</p>}

        {summary !== null && (
          <>
            <p className="lead">
              Each client is priced on its own: the base, plus whatever that client’s plan adds.
              Every price is per month and excludes VAT.
              {!restoring && ' A client’s plan is changed on its Plan tab.'}
            </p>

            <StateBanner summary={summary} />

            <div className="tbl-wrap us-wrap">
              <table className="tbl">
                <caption className="sr-only">Monthly cost by client</caption>
                <thead>
                  <tr>
                    <th scope="col">Client</th>
                    <th scope="col">Plan</th>
                    <th scope="col" className="bl-price">
                      Per month
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {summary.tenants.map((row) => (
                    <tr key={row.tenantId}>
                      <th scope="row">{row.displayName}</th>
                      <td>
                        <ul className="bl-lines">
                          {planLines(row.productIds, products).map((line) => (
                            <li key={line}>{line}</li>
                          ))}
                        </ul>
                      </td>
                      <td className="bl-price">{formatPence(row.totalPence)}</td>
                    </tr>
                  ))}
                  {summary.tenants.length === 0 && (
                    <tr>
                      <td colSpan={3}>
                        <span className="note">
                          No clients yet. Each one you add starts on the base.
                        </span>
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={2}>Account total, per month, excluding VAT</td>
                    <td className="bl-price">{formatPence(summary.totalPence)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="toolbar">
              {/*
                A link rather than a call: the browser downloads it with the
                session cookie attached, and nothing on this page has to
                rebuild the file from JSON.
              */}
              <a className="btn" href={billingApi.billingExportUrl()} download>
                Download per-client CSV
              </a>

              {owner && summary.billingEnabled && !summary.hasSubscription && (
                <button
                  type="button"
                  className="btn btn-primary"
                  aria-busy={leaving}
                  disabled={leaving || summary.tenants.length === 0}
                  onClick={() => void leaveFor(billingApi.billingCheckout)}
                >
                  {leaving ? 'Opening Stripe…' : 'Subscribe'}
                </button>
              )}
              {owner && summary.billingEnabled && summary.hasSubscription && (
                <button
                  type="button"
                  className="btn btn-primary"
                  aria-busy={leaving}
                  disabled={leaving}
                  onClick={() => void leaveFor(billingApi.billingPortal)}
                >
                  {leaving ? 'Opening Stripe…' : 'Manage billing'}
                </button>
              )}
              <span className="status-inline" role="status">
                {actionError}
              </span>
            </div>

            {!summary.billingEnabled && (
              <p className="note">
                Billing is not configured on this deployment, so there is nothing to subscribe to
                yet. The prices above are what each client would cost.
              </p>
            )}
            {summary.billingEnabled && !owner && (
              <p className="note">
                Only an account owner can subscribe or change how this account pays.
              </p>
            )}
            {owner && summary.billingEnabled && !summary.hasSubscription && (
              <p className="hint">
                Subscribe opens Stripe’s checkout for every client’s current plan. Card details go
                to Stripe and never to DriftWatch.
              </p>
            )}
            {owner && summary.billingEnabled && summary.hasSubscription && (
              <p className="hint">
                Manage billing opens Stripe’s portal, for invoices, the payment card and
                cancelling.
              </p>
            )}
          </>
        )}
      </section>

      {!restoring && <Package />}
    </div>
  );
};

/**
 * One banner at most, for the state that decides what happens next. Amber
 * rather than red for the bad ones too: red is a verdict on this console, and
 * an unpaid invoice is not a finding about anybody's tenant.
 */
const StateBanner = ({ summary }: { summary: BillingSummary }) => {
  if (summary.accountState === 'trial') {
    const left = summary.trialEndsAt === null ? null : trialDaysLeft(summary.trialEndsAt, new Date());
    return (
      <div className="callout">
        <span className="callout-title">
          {left === null ? 'Free trial' : `Free trial: ${days(left)} left`}
        </span>
        {summary.billingEnabled
          ? 'Subscribe before the trial ends and assessments carry on without a gap. When it ends without a subscription, scheduled assessments stop; nothing already collected is removed.'
          : 'Nothing is invoiced during the trial.'}
      </div>
    );
  }
  if (summary.accountState === 'past_due') {
    return (
      <div className="callout callout-caution">
        <span className="callout-title">The last payment failed</span>
        Assessments are carrying on for now. An owner can update the payment card under Manage
        billing.
      </div>
    );
  }
  if (summary.accountState === 'suspended') {
    const reason = suspensionReason(summary);
    return (
      <div className="callout callout-caution">
        <span className="callout-title">{reason.title}</span>
        {reason.body} Everything already collected is untouched.
      </div>
    );
  }
  if (summary.accountState === 'closed') {
    return (
      <div className="callout callout-caution">
        <span className="callout-title">This account is closed</span>
        Scheduled assessments have stopped. Everything already collected is untouched.
      </div>
    );
  }
  return null;
};
